import { useState, useEffect } from 'react';

function SSODebug() {
  const [logs, setLogs] = useState([]);
  const [isInIframe, setIsInIframe] = useState(false);
  const [encryptedData, setEncryptedData] = useState('');
  const [waiting, setWaiting] = useState(false);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  useEffect(() => {
    const inIframe = window.self !== window.top;
    setIsInIframe(inIframe);
    addLog(`App loaded. In iframe: ${inIframe}`, inIframe ? 'success' : 'warning');
    addLog(`URL: ${window.location.href}`, 'info');
    addLog(`Referrer: ${document.referrer || 'None'}`, 'info');
  }, []);

  const testSSO = async () => {
    setWaiting(true);
    addLog('Sending REQUEST_USER_DATA to parent window...', 'info');

    try {
      const encryptedUserData = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout after 15 seconds'));
        }, 15000);

        // Listen for any messages
        const messageHandler = ({ data, origin }) => {
          addLog(`Received message: ${JSON.stringify(data)} from ${origin}`, 'info');
          
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            addLog('Got REQUEST_USER_DATA_RESPONSE!', 'success');
            clearTimeout(timeout);
            window.removeEventListener('message', messageHandler);
            resolve(data.payload);
          }
        };

        window.addEventListener('message', messageHandler);

        // Send request
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
        addLog('postMessage sent to parent', 'success');
      });

      setEncryptedData(encryptedUserData);
      addLog(`Received encrypted data (${encryptedUserData?.length || 0} chars)`, 'success');
    } catch (error) {
      addLog(`Error: ${error.message}`, 'error');
    } finally {
      setWaiting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2">GoHighLevel SSO Debug Tool</h1>
          <p className="text-gray-600 mb-4">
            Use this tool to debug SSO integration issues
          </p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded">
              <div className="text-sm text-gray-600">In iframe</div>
              <div className={`text-2xl font-bold ${isInIframe ? 'text-green-600' : 'text-red-600'}`}>
                {isInIframe ? '✅ Yes' : '❌ No'}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded">
              <div className="text-sm text-gray-600">SSO Test Status</div>
              <div className="text-2xl font-bold">
                {waiting ? '⏳ Testing...' : encryptedData ? '✅ Received' : '⚪ Not tested'}
              </div>
            </div>
          </div>

          <button
            onClick={testSSO}
            disabled={waiting}
            className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {waiting ? 'Testing SSO...' : 'Test SSO Communication'}
          </button>

          {encryptedData && (
            <div className="bg-gray-50 p-4 rounded mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Encrypted Data Received:</div>
              <div className="bg-white p-2 rounded border font-mono text-xs break-all">
                {encryptedData.substring(0, 200)}...
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">Event Log</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.map((log, index) => (
              <div
                key={index}
                className={`p-3 rounded text-sm ${
                  log.type === 'error'
                    ? 'bg-red-50 text-red-700'
                    : log.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : log.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-700'
                    : 'bg-gray-50 text-gray-700'
                }`}
              >
                <span className="font-mono text-xs text-gray-500 mr-2">[{log.timestamp}]</span>
                {log.message}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold text-blue-900 mb-2">💡 Troubleshooting Tips</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>
              <strong>Not in iframe:</strong> Make sure you're accessing this page from within GoHighLevel's Custom Page, not directly.
            </li>
            <li>
              <strong>Timeout:</strong> Check that your app is properly configured in GHL marketplace settings with the correct Custom Page URL.
            </li>
            <li>
              <strong>No response:</strong> Verify your app's SSO Shared Secret is correctly set in GHL and matches your backend .env file.
            </li>
            <li>
              <strong>Check console:</strong> Open browser DevTools (F12) and check the Console tab for additional debugging information.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SSODebug;
